import { useRef, useState } from "react";

// Drag files onto a panel to upload them. Wraps any content, so the whole area
// becomes a target rather than just a button.
//
// NOT a 1.0 feature — 1.0 uploads through a file-type dropdown and a plain
// <input type="file" multiple> (Controls/FileUploader.ascx). This is an addition
// on top of that picker, never a replacement for it: the picker stays for people
// who prefer it, for touch devices, and for the case below.
//
// WHAT IT CANNOT DO, and it is worth being straight about because people ask:
// dragging a MESSAGE out of Outlook does not work. Outlook offers the message in
// a private format the browser is not allowed to read, so nothing arrives.
// Dragging a FILE out of Explorer works, and dragging an ATTACHMENT out of an
// open email usually works. To file a whole email, save it first and drag the
// .msg, or use the picker.
//
// Only fires for actual FILES: dragging selected text or a link across the panel
// leaves it alone, otherwise every stray drag flashes the panel as a target.
function hasFiles(event) {
  const types = event.dataTransfer?.types;
  return !!types && Array.from(types).includes("Files");
}

function DropZone({
  onFiles,
  disabled = false,
  label = "Drop files here to upload",
  className = "",
  children,
}) {
  const [over, setOver] = useState(false);
  // Drag events fire for every child element, so a naive enter/leave pair
  // flickers as the pointer crosses them. Counting enters and leaves is what
  // makes the highlight steady.
  const depth = useRef(0);

  if (disabled) return <div className={className}>{children}</div>;

  return (
    <div
      className={`dropzone ${over ? "dropzone-over" : ""} ${className}`.trim()}
      onDragEnter={(e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        depth.current += 1;
        setOver(true);
      }}
      onDragOver={(e) => {
        if (!hasFiles(e)) return;
        e.preventDefault(); // without this the browser navigates to the file
        e.dataTransfer.dropEffect = "copy";
      }}
      onDragLeave={(e) => {
        if (!hasFiles(e)) return;
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setOver(false);
      }}
      onDrop={(e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        depth.current = 0;
        setOver(false);
        const files = Array.from(e.dataTransfer.files || []);
        if (files.length) onFiles(files);
      }}
    >
      {children}
      {over ? <div className="dropzone-overlay"><span>{label}</span></div> : null}
    </div>
  );
}

export default DropZone;
